import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";
import { Plus, Edit3 } from "lucide-react";

const EMPTY = { name: "", category_id: "", description: "", regular_price: 0, offer_price: null, duration_min: null, delivery_mode: "online", tax_rate: 0, status: "active" };

export default function Services() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => { const { data } = await api.get("/crm/services"); setItems(data.items || []); };
  useEffect(() => {
    load();
    api.get("/crm/config/service_categories").then((r) => setCategories(r.data.items || [])).catch(() => {});
  }, []);

  const openNew = () => { setEditingId(null); setForm(EMPTY); setShowModal(true); };
  const openEdit = (s) => {
    setEditingId(s.id);
    setForm({ ...EMPTY, ...s });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const payload = {
      ...form,
      regular_price: Number(form.regular_price) || 0,
      offer_price: form.offer_price ? Number(form.offer_price) : null,
      tax_rate: Number(form.tax_rate) || 0,
      duration_min: form.duration_min ? Number(form.duration_min) : null,
      category_id: form.category_id || null,
    };
    try {
      if (editingId) {
        await api.patch(`/crm/services/${editingId}`, payload);
        toast.success("Service updated");
      } else {
        await api.post("/crm/services", payload);
        toast.success("Service added");
      }
      setShowModal(false); setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e, "Failed")); }
  };

  const setStatus = async (id, status) => { await api.patch(`/crm/services/${id}`, { status }); load(); };

  return (
    <div className="p-6" data-testid="services-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-black text-3xl text-[#0B0D17]">Services</h1>
          <p className="text-sm text-[#4A4D57]">Everything you sell — with pricing.</p>
        </div>
        <button onClick={openNew} className="vp-btn-primary inline-flex items-center gap-1.5 text-sm" data-testid="new-service-btn"><Plus className="w-4 h-4" /> New service</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className="rounded-xl border border-[#E4E5E9] bg-white p-4" data-testid={`service-card-${s.id}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-bold text-[#0B0D17]">{s.name}</h3>
              <span className={"text-[10px] uppercase tracking-widest font-bold " + (s.status === "active" ? "text-[#10B981]" : "text-[#747680]")}>{s.status}</span>
            </div>
            <div className="mt-2 text-sm">
              <span className="font-bold text-[#0B5CFF]">₹{s.offer_price ?? s.regular_price}</span>
              {s.offer_price && <span className="ml-2 text-xs text-[#747680] line-through">₹{s.regular_price}</span>}
            </div>
            <div className="mt-1 text-xs text-[#747680]">{s.duration_min ? `${s.duration_min} min · ` : ""}{s.delivery_mode}</div>
            {s.description && <div className="mt-2 text-xs text-[#4A4D57] line-clamp-2">{s.description}</div>}
            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={() => openEdit(s)} className="text-xs vp-btn-ghost py-1 px-2 inline-flex items-center gap-1" data-testid={`edit-service-${s.id}`}><Edit3 className="w-3 h-3" /> Edit</button>
              <button onClick={() => setStatus(s.id, s.status === "active" ? "inactive" : "active")} className="text-xs vp-btn-ghost py-1 px-2">{s.status === "active" ? "Deactivate" : "Activate"}</button>
              <button onClick={() => { if (window.confirm("Archive this service? Old invoices remain unchanged.")) setStatus(s.id, "archived"); }} className="text-xs text-red-500 hover:underline">Archive</button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full p-10 text-center text-[#747680] rounded-xl border border-dashed border-[#E4E5E9]">No services yet.</div>}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="font-display font-black text-xl mb-4">{editingId ? "Edit service" : "New service"}</h2>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Service name *" className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="svc-name" />
              {categories.length > 0 && (
                <select value={form.category_id || ""} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="svc-category">
                  <option value="">Category…</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full p-3 rounded-lg border border-[#E4E5E9] text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={form.regular_price} onChange={(e) => setForm({ ...form, regular_price: e.target.value })} placeholder="Regular price ₹" className="h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" data-testid="svc-price" />
                <input type="number" value={form.offer_price ?? ""} onChange={(e) => setForm({ ...form, offer_price: e.target.value })} placeholder="Offer price ₹ (optional)" className="h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" value={form.duration_min ?? ""} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} placeholder="Duration (min)" className="h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" />
                <input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} placeholder="Tax %" className="h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm" />
              </div>
              <select value={form.delivery_mode} onChange={(e) => setForm({ ...form, delivery_mode: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-[#E4E5E9] text-sm">
                <option value="online">Online</option><option value="offline">Offline</option>
                <option value="both">Both</option><option value="custom">Custom</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setShowModal(false); setEditingId(null); setForm(EMPTY); }} className="vp-btn-ghost text-sm">Cancel</button>
              <button onClick={save} className="vp-btn-primary text-sm" data-testid="svc-save">{editingId ? "Save" : "Create"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
